// MACHINERY V2 (Stroje) — the catalog browse CHROME composition (split out of
// CatalogBrowseV2.tsx at M2.16c).
//
// >>> WHY THIS FILE EXISTS. <<< The second half of the split-first step
// CatalogOverlays.tsx opened: CatalogBrowseV2 was at 296 of 300 lines and this
// sub-phase adds a whole below-1280 chrome to it. The seam is the same one
// fleet/FleetShell.tsx took from FleetPage.tsx — the PAGE keeps the hooks, the
// handlers and the overlays; this file keeps the chrome the content is wrapped
// in, and owns the 1280 branch.
//
// IT RETURNS A FRAGMENT, NOT A CONTAINER, ON PURPOSE — same reason FleetShell
// does (FleetShell.tsx:18-23): the rail/main flex row and CatalogOverlays must
// stay SIBLINGS inside the page-level div, or the overlays end up one level
// deeper than the row and the popup's "not even a React ancestor of a
// navigating card" guarantee quietly changes shape.
//
// ============================================================
//  THE 1280 BRANCH — AND WHY DESKTOP IS UNTOUCHED
// ============================================================
// Below 1280 CatalogFilterRail DOES NOT RENDER AT ALL — not collapsed, ABSENT.
// mobile/README.md §Overview states the rule for the whole pass ("below desktop,
// the left filter rail disappears… the rail never returns until desktop
// >= ~1280") and the katalog is named in its per-view list, so this is the same
// branch FleetShell.tsx:123 already makes, for the same reason. Its sections
// move into CatalogFilterSheet, which mirrors fleet/FleetFilterSheet.tsx.
//
// The desktop path is the SAME ELEMENTS IN THE SAME ORDER it has always been:
// `{isDesktop && <CatalogFilterRail/>}` renders nothing extra at >= 1280, the
// chrome branch resolves to the identical breadcrumb / title+search / tabs, and
// the scroll area keeps its own `12px 24px 24px`. A desktop user must not be
// able to tell this commit happened.
//
// The 1280 boundary is `useV2MediaQuery(V2_BELOW_DESKTOP)` — the module's single
// breakpoint vocabulary since review finding F-A, and the same string both v2
// stylesheets fence on — never MUI's useMediaQuery and no longer the shared
// integer-width hook. Tablet sits on the MOBILE side of this branch, exactly as
// it does on the fleet.
//
// >>> THE FILTER SHEET'S OPEN FLAG AND ITS DRAFT ARE NOT OWNED BELOW THIS
// BRANCH. <<< They live on CatalogBrowseV2 and arrive as props, because this
// branch UNMOUNTS CatalogMobileChrome when a tablet rotates past 1280 — and a
// pending eight-section selection is user work (review finding F4).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";
import CatalogCategoryTabsV2 from "./CatalogCategoryTabsV2";
import CatalogFilterRail from "./CatalogFilterRail";
import CatalogMobileChrome from "./CatalogMobileChrome";
import { CatalogBreadcrumb, CatalogSearchBox, CatalogTitle } from "./catalogChrome";
import { CatalogFacetModel } from "./catalogFacets";
import { CatalogFilterDraftApi } from "./useCatalogFilterDraft";
import useCatalogBrowse from "./useCatalogBrowse";

export interface CatalogShellProps {
  /** The browse hook's whole surface — one prop instead of eighteen, the same
   *  call FleetShell makes with `data`. */
  c: ReturnType<typeof useCatalogBrowse>;
  facets: CatalogFacetModel;
  brandsExpanded: boolean;
  onToggleBrandsExpanded: () => void;
  expandedParents: Set<string>;
  onToggleParent: (id: string) => void;
  onSelectCategory: (code?: string) => void;
  onClearAll: () => void;
  activeFilters: number;
  /** Board 19's legend, opened from the mobile page head. */
  onOpenLegend: () => void;
  /** The filter sheet's open flag — page-owned, see the header (F4). */
  sheetOpen: boolean;
  onSheetOpenChange: (open: boolean) => void;
  /** The staged filter draft — page-owned for the same reason. */
  filterApi: CatalogFilterDraftApi;
  /** The scroll area's body — showing line, grid, pagination, retry. */
  children: React.ReactNode;
}

const CatalogShell: React.FC<CatalogShellProps> = ({
  c,
  facets,
  brandsExpanded,
  onToggleBrandsExpanded,
  expandedParents,
  onToggleParent,
  onSelectCategory,
  onClearAll,
  activeFilters,
  onOpenLegend,
  sheetOpen,
  onSheetOpenChange,
  filterApi,
  children,
}) => {
  const { translate } = useTranslation("myMachinery");
  // F-A: below 1280 is read as the media QUERY both v2 stylesheets fence on,
  // never as an integer width. Identical at every integer width; correct in
  // the fractional band too. See useV2MediaQuery.ts.
  const isDesktop = !useV2MediaQuery(V2_BELOW_DESKTOP);
  const { query, setQuery } = c;

  return (
    <>
      {isDesktop && (
        <CatalogFilterRail
          query={query}
          onQueryChange={setQuery}
          brandSearch={c.brandSearch}
          onBrandSearchChange={c.setBrandSearch}
          facets={facets}
          brandsExpanded={brandsExpanded}
          onToggleBrandsExpanded={onToggleBrandsExpanded}
          expandedParents={expandedParents}
          onToggleParent={onToggleParent}
          totalCount={c.browseTotalCount}
          taperTypes={c.taperTypes}
          operationTypes={c.operationTypes}
          controlSystemBrands={c.controlSystemBrands}
        />
      )}

      <main
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {isDesktop ? (
          <>
            <CatalogBreadcrumb />
            <CatalogTitle>
              <CatalogSearchBox
                value={c.searchValue}
                onChange={c.onSearchChange}
                onSubmit={c.onSearchSubmit}
                onClear={c.clearSearch}
              />
            </CatalogTitle>

            <div style={{ padding: "12px 24px 0", minWidth: 0 }}>
              <CatalogCategoryTabsV2
                categories={c.categories}
                selectedCode={query.categoryCode}
                onSelect={onSelectCategory}
              />
            </div>
          </>
        ) : (
          <CatalogMobileChrome
            c={c}
            facets={facets}
            onSelectCategory={onSelectCategory}
            onOpenLegend={onOpenLegend}
            sheetOpen={sheetOpen}
            onSheetOpenChange={onSheetOpenChange}
            filterApi={filterApi}
          />
        )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isDesktop ? "12px 24px 24px" : "4px 14px 16px",
          }}
        >
          {/* DESKTOP ONLY. Below 1280 the same information is the Filtre
              badge plus the dismissible chip row (pattern B row 2), which say
              more in less space — a count plus a Clear-all would be a third
              statement of the same fact directly under them. */}
          {isDesktop && activeFilters > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--faint)",
                  letterSpacing: "0.04em",
                }}
              >
                {translate("myMachinery:v2.catalog.activeFilters", { count: activeFilters })}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClearAll}
                style={{ height: 26, fontSize: 11.5 }}
              >
                {translate("myMachinery:clearAll")}
              </button>
            </div>
          )}

          {children}
        </div>
      </main>
    </>
  );
};

export default CatalogShell;
